import { FC } from "react";
import {
  OakMaxWidth,
  OakBox,
  OakBreadcrumbs,
} from "@oaknational/oak-components";

import { TopNavProps } from "../AppComponents/TopNav/TopNav";

import PostCategoryList, {
  CATEGORY_NAV_LABEL,
  PostCategoryPage,
} from "@/components/SharedComponents/PostCategoryList/PostCategoryList";
import { PostListJsonLd } from "@/browser-lib/seo/getJsonLd";
import { getSeoProps } from "@/browser-lib/seo/getSeoProps";
import { SeoProps } from "@/browser-lib/seo/Seo";
import {
  PostListingPage,
  BlogWebinarCategory,
  SerializedBlogPostPreview,
  SerializedWebinarPreview,
} from "@/common-lib/cms-types";
import { WebinarsListingPage } from "@/common-lib/cms-types/webinarsListingPage";
import PostListAndCategories from "@/components/GenericPagesViews/PostListAndCategories.view";
import {
  CrumbPageVariant,
  getBlogWebinarListBreadcrumbs,
} from "@/components/SharedComponents/Breadcrumbs/getBreadcrumbs";
import SummaryCard from "@/components/SharedComponents/Card/SummaryCard";
import Layout from "@/components/AppComponents/AppLayout";
import MobileFilters from "@/components/SharedComponents/MobileFilters";
import {
  PostListingPageProps,
  blogToPostListItem,
} from "@/components/GenericPagesViews/BlogIndex.view";
import {
  WebinarListingPageProps,
  webinarToPostListItem,
} from "@/components/GenericPagesViews/WebinarsIndex.view";
import { TeacherBrowseAnalyticsStoreProvider } from "@/context/TeacherBrowseAnalytics/TeacherBrowseAnalyticsProvider";

type PostListingProps = {
  seo: SeoProps;
  pageData: WebinarsListingPage | PostListingPage;
  categories: BlogWebinarCategory[];
  categorySlug: string | null;
  postsWithCategories: WebinarListingPageProps | PostListingPageProps;
  posts: SerializedBlogPostPreview[] | SerializedWebinarPreview[];
  page: PostCategoryPage;
  variant: {
    slug: CrumbPageVariant;
    title: string;
  };
  topNav: TopNavProps;
};

const PostListing: FC<PostListingProps> = ({
  categories,
  categorySlug,
  seo,
  pageData,
  postsWithCategories,
  posts,
  variant,
  page,
  topNav,
}) => {
  const categoryHeading = categories.find(
    (cat) => cat.slug === categorySlug,
  )?.title;

  const postListItems = posts.map((post) =>
    "video" in post ? webinarToPostListItem(post) : blogToPostListItem(post),
  );

  return (
    <TeacherBrowseAnalyticsStoreProvider
      programmeState={null}
      accessLevel="blogs_embedded_links"
    >
      <Layout
        seoProps={getSeoProps(seo)}
        $background="bg-primary"
        topNavProps={topNav}
      >
        <OakMaxWidth $pt={"spacing-20"} $display={["none", "flex"]}>
          <OakBreadcrumbs
            breadcrumbs={getBlogWebinarListBreadcrumbs(
              categories,
              categorySlug,
              variant.slug,
              variant.title,
            )}
          />
        </OakMaxWidth>
        <OakMaxWidth
          $mb={["spacing-56", "spacing-80"]}
          $pt={["spacing-0", "spacing-24", "spacing-24"]}
        >
          <OakBox $pa={["spacing-12", "spacing-0", "spacing-0"]}>
            <SummaryCard
              {...pageData}
              heading={categoryHeading || pageData.heading}
            />
            <MobileFilters page={page} label={CATEGORY_NAV_LABEL}>
              <PostCategoryList
                $pv={"spacing-24"}
                $ph={"spacing-16"}
                categories={categories}
                selectedCategorySlug={categorySlug}
                page={page}
              />
            </MobileFilters>
          </OakBox>

          <PostListAndCategories
            {...postsWithCategories}
            blogs={postListItems}
            page={page}
            topNav={topNav}
          />
        </OakMaxWidth>
        <PostListJsonLd blogs={posts} />
      </Layout>
    </TeacherBrowseAnalyticsStoreProvider>
  );
};
export default PostListing;
